/**
 * auth-me.ts
 * Shared Zod contract for GET /auth/me response — KI-11 / KI-20 / KI-31
 *
 * Lives in @suite/contracts-kernel because it is an identity/auth DTO shared
 * by the backend (kernel handler) and the frontend (getMe() API method).
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * Spec: KI-11 / KI-20 / design §1.4 / KI-31 (memberships additive field)
 */
import { z } from 'zod';
import { SignInMethodSchema } from './sign-in-method.js';

// ---------------------------------------------------------------------------
// T03 — MembershipEntrySchema
// ---------------------------------------------------------------------------

/**
 * A single membership entry in the /auth/me response.
 * KI-31: each entry represents this account's membership in one company, including
 * the per-membership active flag and the roles held in that company.
 * The active flag is per-membership (not account-level): `account.active=false` would
 * block the session before /auth/me is reached; here `active=false` means this
 * company membership was deactivated while others remain valid.
 */
export const MembershipEntrySchema = z.object({
  companyId: z.string().uuid(),
  companyName: z.string(),
  /** Whether the membership (app_user row) for this company is active. */
  active: z.boolean(),
  /**
   * Whether the company itself is active (identity.company.active). A membership
   * can be active while its company is deactivated — the frontend company switcher
   * must never offer a deactivated company as a switch target.
   */
  companyActive: z.boolean(),
  /** Roles held in this company (from identity.user_role → role.name). */
  roles: z.array(z.string()),
});

export type MembershipEntry = z.infer<typeof MembershipEntrySchema>;

// ---------------------------------------------------------------------------
// AuthMeResponseSchema (extended with memberships — KI-31, additive)
// ---------------------------------------------------------------------------

export const AuthMeResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    roles: z.array(z.string()),
  }),
  company: z.object({
    id: z.string().uuid(),
    name: z.string(),
    /**
     * CUIT for this company (CP-2). Nullable — not all companies have it set yet.
     */
    cuit: z.string().nullable().optional(),
    /**
     * Relative URL of the company logo proxy endpoint.
     * Set to '/identity/company/logo' when the company has a logo_file_id, else null.
     * CP-LOGO: frontend fetches this URL with the Bearer token.
     */
    logoUrl: z.string().nullable().optional(),
    /**
     * Relative URL of the dark-mode logo variant proxy endpoint.
     * Set to '/identity/company/logo?variant=dark' when the company has a
     * logo_file_id_dark, else null. CP-LOGO-DARK.
     */
    logoUrlDark: z.string().nullable().optional(),
    /** How the company's users sign in — the user admin hides the password field for SSO. */
    signInMethod: SignInMethodSchema.optional(),
  }),
  /**
   * Module keys for which the current tenant has a commercial entitlement.
   * Resolved server-side from catalog.feature_flag (module.<key>.enabled) inside
   * runInTenantTx. Empty array = no modules entitled (edge case / new company).
   * KI-20 / design §8 Phase 1a.
   */
  entitledModules: z.array(z.string()),
  /**
   * All memberships for this account across all companies.
   * KI-31.1: includes both active and inactive memberships so the frontend
   * CompanySwitcher can show all companies and their status.
   * KI-31.4: required field (not optional) — backend always populates it.
   */
  memberships: z.array(MembershipEntrySchema),
});

export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;
